import { useEffect, useRef, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Tab, Nav } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { showToast } from "redux/slices/Toasts";
import { RootState } from "redux/store";
import { FormInput, Title, Spinner, Modal } from "components";
import useToggle from "hooks/useToggle";
import { cleanCatalogueDetails, createCatalogue, getCatalogueDetails, updateCatalogue, updateCatalogueField, updateCatalogueModelField } from "redux/slices/Catalogues";
import { RemoveTimeFromDate } from "helpers/data/dataHandling";
import Stocks from "./Stocks";
import Stores from "./Stores";
import { ManagedCatalogueAddUpdate } from "models/dbModels/nanagedCatalogues/ManagedCatalogue";

export function NewCatalogue() {
	const form = useRef(null);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { catalogueId } = useParams();
	const [isValidated, setValidated] = useToggle();
	const [showConfirm, setShowConfirm] = useState<boolean>(false);
	const [isNewCatalogue, setIsNewCatalogue] = useState<boolean>(true);
	const catalogue = useAppSelector((state: RootState) => state.catalogues);
	const currentStoreGroupId = useAppSelector((state: RootState) => state.global.storeGroupId);

	useEffect(() => {
		if (catalogueId && !isNaN(Number.parseInt(catalogueId))) {
			dispatch(getCatalogueDetails(
				{
					loadDetails: ["Stock", "Stores"],
					ids: [Number.parseInt(catalogueId)],
				})).then((res) => {
					handleChange("loadingStocks", false);
				});
			setIsNewCatalogue(false);
		} else {
			navigate("/app/catalogue/new");
			dispatch(cleanCatalogueDetails());
			setIsNewCatalogue(true);
		}
	}, [catalogueId]);

	function handleStockClick(stockId: any) {
		let currentStockId = catalogue.model.catalogueStocks.findIndex((item: any) => item.productId === stockId);
		console.log("index", currentStockId);
		handleChange("currentStockSelected", currentStockId);
		handleChange("stockPopup", true)
	}

	function handleSubmit() {
		setShowConfirm(false);
		handleChange("loading", true);
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			if (isNewCatalogue) {
				dispatch(createCatalogue([catalogue.model])).then(() => handleUpdate());
			} else {
				dispatch(updateCatalogue([catalogue.model])).then(() => handleUpdate());
			}
		} else {
			handleChange("loading", false);
		}
		setValidated();
	}

	function handleUpdate() {
		handleChange("loading", false);
		setTimeout(() => {
			navigate("/app/catalogues");
		}, 500);
	}

	function handleChange(option: any, optionValue: any) {
		dispatch(updateCatalogueField({ key: option, value: optionValue }));
	}

	function handleModelChange(option: keyof ManagedCatalogueAddUpdate, optionValue: any) {
		dispatch(updateCatalogueModelField({ key: option, value: optionValue }));
	}

	return (
		<>
			<Title pageTitle={isNewCatalogue ? "New Catalogue" : "Update Catalogue"} crumbs={[{ name: "Catalogues", path: "/app/catalogues" }]} />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Col>
									<Form onSubmit={handleSubmit} validated={isValidated} ref={form}>
										<Row>
											<h4 className="mt-2">Catalogue Details</h4>
											<Col md={6}>
												<Col md={8}>
													<FormInput
														required
														label="Name"
														type="text"
														name="catalogue-name"
														placeholder="Enter catalogue name"
														containerClass={"mb-3"}
														value={catalogue.model.name}
														key="text"
														onChange={(e) => handleModelChange("name", e.target.value)}
													/>
												</Col>
											</Col>
											<Col md={6} className="d-flex flex-row-reverse">
												<Col md={4} className="ms-3">
													<FormInput
														min={catalogue.model.startDate ?? undefined}
														label="End Date"
														type="date"
														name="catalogue-end-date"
														placeholder="Enter catalogue end date"
														containerClass={"mb-3"}
														value={RemoveTimeFromDate(catalogue.model.endDate)}
														onChange={(e) => handleModelChange("endDate", e.target.value === "" ? null : e.target.value)}
													/>
												</Col>
												<Col md={4}>
													<FormInput
														max={catalogue.model.endDate ?? undefined}
														label="Start Date"
														type="date"
														name="catalogue-start-date"
														placeholder="Enter catalogue start date"
														containerClass={"mb-3"}
														value={RemoveTimeFromDate(catalogue.model.startDate)}
														onChange={(e) => handleModelChange("startDate", e.target.value === "" ? null : e.target.value)}
													/>
												</Col>
											</Col>
										</Row>
										<Row className="ms-0 me-0 mb-0" >
											<Col md={12}>
												<Modal
													title="Confirm"
													show={showConfirm}
													buttonType="primary"
													buttonText={"Continue"}
													buttonCloseText="Cancel"
													handleSubmit={handleSubmit}
													handleClose={() => setShowConfirm(false)}
													description={
														<>
															<p>You are about to sync the catalogue down to the selected store(s).</p>
															<p>Are you sure you want to continue?</p>
														</>
													}
												/>
												<Tab.Container defaultActiveKey="1">
													<Row className="mb-1">
														<Nav id="stocks-nav" as="ul" variant="pills" className="nav nav-pills bg-nav-pills nav-justified mb-3">
															<Nav.Item as="li" className="nav-item">
																<Nav.Link href="#" eventKey="1" className="nav-link rounded-0">Stocks</Nav.Link>
															</Nav.Item>
															<Nav.Item as="li">
																<Nav.Link href="#" eventKey="2" className="nav-link rounded-0">Stores</Nav.Link>
															</Nav.Item>
														</Nav>
														<Row style={{ margin: "0px", padding: "0px" }}>
															<Col lg={12} style={{ padding: "0px 3px" }}>
																<Tab.Content>
																	<Tab.Pane eventKey="1">
																		<Stocks />
																	</Tab.Pane>
																	<Tab.Pane eventKey="2">
																		<Stores />
																	</Tab.Pane>
																</Tab.Content>
															</Col>
														</Row>
													</Row>
												</Tab.Container>
											</Col>
										</Row>
										<Row className="mt-2">
											<Col sm={6}>
												<Link to="/app/catalogues" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold" onClick={() => dispatch(cleanCatalogueDetails())}>
													<i className="mdi mdi-arrow-left"></i> Back to All Catalogues {" "}
												</Link>
											</Col>
											<Col sm={6} className="d-flex flex-row-reverse">
												<Button variant="primary" className="btn cta-button" disabled={catalogue.loading} onClick={() => { setShowConfirm(true)}}>
													{catalogue.loading ? (
														<><Spinner className="spinner-border-sm me-1" tag="span" color="white" />Saving...</>
													) : <>Save</>
													}
												</Button>
											</Col>
										</Row>
									</Form>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</>
	);
}